import { IManageArticle, IManageArticleAction } from 'models/magazine/article/managearticle';
import { IArticle } from 'models/magazine/article/article';
import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
const { notifSend } = notifActions;
import { IArticleTagDomainValue } from 'models/magazine/article/articletagdomainvalue';
import { ArticleStatus } from 'models/enums/statuses';
import { IImageData, ICropImageData } from 'models/common';
import { IArticleProductItem } from 'models/magazine/article/articleproductitem';
import { change } from 'redux-form';
import * as c from 'models/constants';

/** Modal Module actions */
import { closeModal } from 'modules/modal';

/** Action Types */
export const REQUEST_START: string = 'managearticle/REQUEST_START';
export const REQUEST_FAILURE: string = 'managearticle/REQUEST_FAILURE';
export const GET_ARTICLE_SUCCESS: string = 'managearticle/GET_ARTICLE_SUCCESS';
export const UPDATE_ARTICLE_SUCCESS: string = 'managearticle/UPDATE_ARTICLE_SUCCESS';
export const SET_IS_EDIT_MODE: string = 'managearticle/SET_IS_EDIT_MODE';
export const RESET_DATA: string = 'managearticle/RESET_DATA';
export const GET_ARTICLETAGS_SUCCESS: string = 'managearticle/GET_ARTICLETAGS_SUCCESS';
export const CLEAR_FORM_DATA: string = 'managearticle/CLEAR_FORM_DATA';
export const SET_COVERIMAGES_TAB: string = 'managearticle/SET_COVERIMAGES_TAB';
export const SET_FRIENDLY_URL_ALREADY_EXISTS: string = 'managearticle/SET_FRIENDLY_URL_ALREADY_EXISTS';
export const VALIDATION_ERROR: string = 'managearticle/VALIDATION_ERROR';
export const CLEAR_FORM_ERROR: string = 'managearticle/CLEAR_FORM_ERROR';
export const SET_ORIGINAL_ARTICLE_ITEMS: string = 'managearticle/SET_ORIGINAL_ARTICLE_ITEMS';
export const SET_USERTOKENFORUPLOAD: string = 'managearticle/SET_USERTOKENFORUPLOAD';

/** Initial State */
const initialState: IManageArticle = {
	isFetching: false,
	article: {
		listed: true,
		commentsEnabled: true,
		location: '',
		status: ArticleStatus.Draft,
		body: '',
		addedDate: null,
		coverImages: [],
		items: '[]',
		itemsJArray: [],
	},
	articleTags: [],
	selectedCoverImageTab: 'slide_0',
	originalArticleItems: [],
	userTokenForUpload: null,
};

/** Reducer */
export function manageArticleReducer(state = initialState, action: IManageArticleAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
				friendlyUrlAreadyExists: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
				friendlyUrlAreadyExists: false,
			});

		case GET_ARTICLE_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				article: action.payload.article,
			});

		case UPDATE_ARTICLE_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				article: action.payload.article,
			});

		case GET_ARTICLETAGS_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				articleTags: action.payload.articleTags,
			});

		case SET_IS_EDIT_MODE:
			return Object.assign({}, state, {
				isEditMode: action.payload.isEditMode,
			});

		case SET_COVERIMAGES_TAB:
			return Object.assign({}, state, {
				selectedCoverImageTab: action.payload.selectedCoverImageTab,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				article: {
					listed: true,
					commentsEnabled: true,
					location: '',
					status: ArticleStatus.Draft,
					body: '',
					items: '',
					itemsJArray: [],
				},
				originalArticleItems: [],
			});

		case VALIDATION_ERROR:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case CLEAR_FORM_ERROR:
			return Object.assign({}, state, {
				isFetching: false,
				message: null,
				error: false,
			});

		case SET_ORIGINAL_ARTICLE_ITEMS:
			return Object.assign({}, state, {
				originalArticleItems: action.payload.originalArticleItems,
			});

		case SET_USERTOKENFORUPLOAD:
			return Object.assign({}, state, {
				userTokenForUpload: action.payload.userTokenForUpload,
			});

		default:
			return state;
	}
}

/** Action Creator - create */
export function submitCreateArticle(article: IArticle, approve?: boolean) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		article.userId = article.userId ? article.userId : getState().userPreferences.id;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(article),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newArticle: IArticle) => {
						dispatch(getArticleSuccess(newArticle));
						dispatch(setIsEditMode(true));
						if (approve) {
							dispatch(setArticleStatus(newArticle.id, ArticleStatus.InApproval));
						}
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_Article_Created'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(getArticleSuccess(article));
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function submitUpdateArticle(article: IArticle, approve?: boolean) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/' + article.id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(article),
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(updateArticleSuccess(article));
					if (approve === true) {
						dispatch(setArticleStatus(article.id, ArticleStatus.InApproval));
					}
					dispatch(
						notifSend({ message: i18n.t('Notification_Success_Article_Updated'), kind: 'success', dismissAfter: 3000 }),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(getArticleSuccess(article));
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getArticle(id: string) {
	return (dispatch, getState: () => IStore) => {
		if (id) {
			dispatch(setIsEditMode(true));
			dispatch(requestStart());

			return fetch(
				getState().fetchDefault.baseAPI + 'api/Article/' + id + '/Edit',
				Object.assign({}, getState().fetchDefault.requestInit),
			)
				.then((res) => {
					if (res.ok) {
						return res
							.json()
							.then((res: IArticle) => {
								dispatch(getArticleSuccess(res));
								const items = res.itemsJArray.map((x) => {
									return {
										...x,
									};
								});
								dispatch(setOriginalArticleItems([...items]));
							})
							.then(() => {
								dispatch(getArticleTags());
								dispatch(getUserTokenForUpload());
							});
					} else {
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								dispatch(requestFailure(res));
							})
							.catch(() => {
								const message: any = { status, statusText };
								dispatch(requestFailure(message));
							});
					}
				})
				.catch((err) => {
					dispatch(requestFailure(err));
				});
		} else {
			dispatch(setIsEditMode(false));
			dispatch(clearFormData());
			dispatch(getArticleTags());
			dispatch(getUserTokenForUpload());
		}
	};
}

/** Action Creator */
export function getArticleTags() {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ArticleTag',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => dispatch(getArticleTagsSuccess(res)));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function setArticleStatus(id: string, status: ArticleStatus) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/' + id + '/SetStatus/' + status,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
			}),
		)
			.then((res) => {
				if (res.ok) {
					if (getState().manageArticle.article) {
						dispatch(getArticle(id));
					}

					if (status === ArticleStatus.Approved) {
						dispatch(clearManageArticleFormError());
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_Article_Approved'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
					}
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function cropAndSaveCoverImage(image: ICropImageData) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imageupload/crop',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newImage: IImageData) => {
						dispatch(change(c.FORM_NAME_MANAGE_ARTICLE, 'pictureTitle', null));
						dispatch(change(c.FORM_NAME_MANAGE_ARTICLE, 'coverImages', []));
						dispatch(change(c.FORM_NAME_MANAGE_ARTICLE, 'picture', newImage.href));
						dispatch(change(c.FORM_NAME_MANAGE_ARTICLE, 'pictureId', newImage.id));
						dispatch(closeModal());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function cropAndSaveSliderImage(image: ICropImageData, slideIndex: number, fieldName: string, formName: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imageupload/crop',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((image: IImageData) => {
						dispatch(change(formName, `${fieldName}[${slideIndex}].href`, image.href));
						dispatch(change(formName, `${fieldName}[${slideIndex}].id`, image.id));
						dispatch(closeModal());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function getUserTokenForUpload() {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imagetoken/GetUserTokenForUpload',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.text().then((token) => {
						dispatch(setImageTokenForUpload(token));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getArticleTagsSuccess(articleTags: IArticleTagDomainValue[]): IManageArticleAction {
	return {
		type: GET_ARTICLETAGS_SUCCESS,
		payload: {
			articleTags,
		},
	};
}

/** Action Creator */
export function requestStart(): IManageArticleAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IManageArticleAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getArticleSuccess(article: IArticle): IManageArticleAction {
	// if we have more than one image show cover images tabs
	article.displayCoverSliderFields = article.coverImages.length > 1 ? true : false;
	return {
		type: GET_ARTICLE_SUCCESS,
		payload: {
			article,
		},
	};
}

/** Action Creator */
export function updateArticleSuccess(article: IArticle): IManageArticleAction {
	// if we have more than one image show cover images tabs
	article.displayCoverSliderFields = article.coverImages.length > 1 ? true : false;
	return {
		type: UPDATE_ARTICLE_SUCCESS,
		payload: {
			article,
		},
	};
}

/** Action Creator */
export function setIsEditMode(isEditMode: boolean): IManageArticleAction {
	return {
		type: SET_IS_EDIT_MODE,
		payload: {
			isEditMode,
		},
	};
}

/** Action Creator */
export function setCoverImagesTab(tab: string): IManageArticleAction {
	return {
		type: SET_COVERIMAGES_TAB,
		payload: {
			selectedCoverImageTab: tab,
		},
	};
}

/** Action Creator */
export function clearFormData(): IManageArticleAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function clearManageArticleFormError(): IManageArticleAction {
	return {
		type: CLEAR_FORM_ERROR,
	};
}

/** Action Creator */
export function setManageArticleValidationFailure(error: any): IManageArticleAction {
	return {
		type: VALIDATION_ERROR,
		payload: {
			message: error,
		},
	};
}

/** Action Creator */
export function setOriginalArticleItems(originalArticleItems: IArticleProductItem[]): IManageArticleAction {
	return {
		type: SET_ORIGINAL_ARTICLE_ITEMS,
		payload: {
			originalArticleItems,
		},
	};
}

/** Action Creator */
export function setImageTokenForUpload(userTokenForUpload: string): IManageArticleAction {
	return {
		type: SET_USERTOKENFORUPLOAD,
		payload: {
			userTokenForUpload,
		},
	};
}
